/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  TextField,
  Stack,
  Typography,
  IconButton,
  Box,
  Chip,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
  Menu,
  Tooltip,
} from '@mui/material';
import { Iconify } from 'src/components';
import { useSelector } from 'src/redux/store';
import { callApi } from 'src/utils/apiCall';
import { getPracticeId } from 'src/redux/slices/clincheck';
import WhatsAppAudienceRulesEditor from 'src/components/whatsappComponents/WhatsAppAudienceRulesEditor';
import { toRuleValue, toPayload, validateRule } from 'src/components/whatsappComponents/audienceRulesUtils';
import useAudiencePermission from 'src/components/whatsappComponents/useAudiencePermission';
import WhatsAppTemplatePreviewPanel from 'src/components/whatsappComponents/WhatsAppTemplatePreviewPanel';

const DEFAULT_LANGUAGE = 'en_GB';
const LIMITS = { HEADER: 60, BODY: 1024, FOOTER: 60 };

// Extract real practice details for merge tag examples
const getPracticeExamples = (practice, bookingUrl) => {
  const phone = practice?.contact?.phone || '';
  const addr = practice?.address;
  const addressStr = addr
    ? [addr.address_line_1, addr.city, addr.postcode].filter(Boolean).join(', ')
    : '';
  // Branded forms base — comes from the practice's
  // ``consent_form_config`` PracticeChoice (resolved server-side by
  // ``apps.practice.form_link.resolve_public_form_base_url``). When the
  // FE doesn't have it yet (e.g. editing offline), we fall back to a
  // safe placeholder for the URL-button preview only — the actual send
  // resolves the real base on the server.
  const formsBaseUrl =
    practice?.consent_form_config?.base_url
    || practice?.forms_base_url
    || 'https://forms.example.com';
  return {
    name: practice?.name || 'Smile Rooms',
    phone: phone || '020 7123 4567',
    address: addressStr || '123 High Street',
    bookingUrl: bookingUrl || '',
    formsBaseUrl,
  };
};

const buildMergeTags = (practiceExamples, { isFormsTemplate = false } = {}) => {
  const baseTags = [
    { key: 'first_name', label: 'First Name', icon: 'mdi:account-outline', example: 'Sarah' },
    // Sender merge tag — resolves to the logged-in user's first name (matches the
    // email mechanism's `user_first_name` key, fed from User.get_context()).
    { key: 'user_first_name', label: 'Your First Name', icon: 'mdi:account-tie-outline', example: 'Sarah' },
    { key: 'time_of_day', label: 'Time of Day', icon: 'mdi:clock-time-four-outline', example: 'morning' },
    { key: 'treatment_name', label: 'Treatment', icon: 'mdi:tooth-outline', example: 'Invisalign' },
    { key: 'practice_name', label: 'Practice Name', icon: 'mdi:hospital-building', example: practiceExamples.name },
    { key: 'practice_phone', label: 'Practice Phone', icon: 'mdi:phone-outline', example: practiceExamples.phone },
    { key: 'practice_address', label: 'Address', icon: 'mdi:map-marker-outline', example: practiceExamples.address },
  ];
  // ``form_url`` is only useful for FORMS-tagged templates (the Send Forms
  // wizard substitutes it with a unique per-patient consent link). Hide it
  // from non-FORMS templates so the chip rail stays uncluttered. Backend
  // context key matches ``apps.practice.template_context.build_consent_
  // template_context`` exactly — do not rename without updating that.
  if (isFormsTemplate) {
    baseTags.push({
      key: 'form_url',
      label: 'Form Link',
      icon: 'mdi:link-variant',
      example: 'https://forms.example.com/consent/abc123',
    });
  }
  return baseTags;
};

// CTA presets. Order matters — rendered as a horizontal pill row.
// ``form_link`` is gated to FORMS-tagged templates (filtered at render
// time below) because its URL pattern hardcodes the consent path and
// only makes sense for the Send Forms wizard. Meta only allows variables
// in the URL-button SUFFIX (not in the protocol+domain prefix), so the
// preset auto-fills ``https://{base}/consent/{{1}}`` and the runtime
// substitutes ``form_url_token`` (just the access token) at send time —
// see ``apps/whatsapp/models.py:generate_template_context_mapping`` for
// the URL-button mapping logic.
const BUTTON_TYPES = [
  { value: 'none', label: 'No button', icon: 'mdi:close-circle-outline' },
  { value: 'url', label: 'Book Online', icon: 'mdi:calendar-check-outline' },
  { value: 'form_link', label: 'Send Form', icon: 'mdi:file-document-edit-outline', formsOnly: true },
  { value: 'phone', label: 'Call Us', icon: 'mdi:phone-outline' },
];

// Suffix-only URL pattern Meta will accept. The ``{{1}}`` slot is the
// access token; the static prefix is the practice's branded form base.
// We set a default base for the example payload; per-practice resolution
// happens server-side at send time via ``form_url_token`` substitution.
const FORM_LINK_URL_PATTERN = (base) => `${(base || 'https://forms.example.com').replace(/\/$/, '')}/consent/{{1}}`;
const FORM_LINK_EXAMPLE_TOKEN = 'abc123token';

const ALLOWED_USAGE_TYPES = [
  { value: 'FOLLOW_UP', label: 'Follow Up', icon: 'mdi:message-reply-text-outline' },
  { value: 'REMINDER', label: 'Reminder', icon: 'mdi:bell-outline' },
  { value: 'COMMUNICATION', label: 'Communication', icon: 'mdi:chat-outline' },
  { value: 'MARKETING', label: 'Marketing', icon: 'mdi:bullhorn-outline' },
  { value: 'PROMOTION', label: 'Promotion', icon: 'mdi:tag-outline' },
  // Marks a template as eligible for the Send Forms wizard's WhatsApp
  // channel — the wizard filters templates by ``usage_type=FORMS`` so the
  // chosen template carries the form-link merge tag (``$form_url``) into
  // the Meta-approved body. Backend support: practice migration 0307,
  // engagement migration 0017, whatsapp migration 0047.
  { value: 'FORMS', label: 'Forms', icon: 'mdi:file-document-edit-outline' },
  { value: 'OTHER', label: 'Other', icon: 'mdi:dots-horizontal-circle-outline' },
];

const META_CATEGORIES = [
  { value: 'UTILITY', label: 'Utility', icon: 'mdi:wrench-outline', desc: 'Appointment reminders, booking confirmations, follow-ups' },
  { value: 'MARKETING', label: 'Marketing', icon: 'mdi:bullhorn-outline', desc: 'Promotions, offers, newsletters' },
];

const HEADER_FORMATS = [
  { value: 'TEXT', label: 'Text', icon: 'mdi:format-text' },
  { value: 'IMAGE', label: 'Image', icon: 'mdi:image-outline' },
];

const INITIAL_FORM = {
  displayName: '',
  headerFormat: 'TEXT',
  headerText: '',
  bodyText: '',
  footerText: '',
  buttonType: 'none',
  buttonLabel: 'Book Now',
  buttonUrl: '',
  buttonPhone: '',
  usageType: 'FOLLOW_UP',
  category: 'UTILITY',
};

const toTemplateName = (input) =>
  input
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_{2,}/g, '_')
    .replace(/^_|_$/g, '');

// Match either positional ``{{N}}`` or named ``{{name}}`` template variables.
// Meta's WhatsApp Cloud API supports both forms and treats them as mutually
// exclusive WITHIN A SINGLE COMPONENT (a BODY cannot mix positional and named).
const extractVarTokens = (text) => {
  const matches = text.match(/\{\{(?:\d+|[a-zA-Z_][a-zA-Z0-9_]*)\}\}/g) || [];
  return matches.map((m) => m.slice(2, -2));
};

const countVariables = (text) => extractVarTokens(text).length;

const fixLeadingTrailingVars = (text) => {
  let fixed = text;
  if (/^\s*\{\{(?:\d+|[a-zA-Z_][a-zA-Z0-9_]*)\}\}/.test(fixed)) fixed = `Hi ${fixed.trimStart()}`;
  if (/\{\{(?:\d+|[a-zA-Z_][a-zA-Z0-9_]*)\}\}\s*$/.test(fixed)) fixed = `${fixed.trimEnd()}.`;
  return fixed;
};

const getNextVarNumber = (form) => {
  const all = `${form.headerText} ${form.bodyText} ${form.footerText}`;
  // Only positional `{{N}}` tokens factor into "next number" — named tokens
  // (`{{lead_first_name}}`) don't follow a numeric sequence.
  const matches = all.match(/\{\{(\d+)\}\}/g) || [];
  if (matches.length === 0) return 1;
  const nums = matches.map((m) => parseInt(m.replace(/\{|\}/g, ''), 10));
  return Math.max(...nums) + 1;
};

// Map a body/header's ``{{N}}`` (or ``{{name}}``) placeholders to the chip
// key that was inserted at that position. The backend's
// ``WhatsappMessageTemplate.generate_template_context_mapping`` reads
// ``example.body_text[0]`` (or ``body_text_named_params``) and uses those
// values as CONTEXT-DICT KEYS when substituting at send time. Sending
// generic ``["Example 1", "Example 2"]`` strings would break runtime
// substitution (every variable would render as ``-``). For positional
// variables we look up the chip key by index; for named variables the
// token itself IS the param name.
const _exampleValuesForText = (text, tagMap) => extractVarTokens(text).map((tok) => {
  if (/^\d+$/.test(tok)) {
    const num = parseInt(tok, 10);
    const tag = tagMap?.[num];
    return tag?.key || `var${num}`;
  }
  return tok; // named variable — token is the param name (e.g. lead_first_name)
});

// Returns true if any extracted variable in ``text`` is named (non-digit).
// Meta requires components to be entirely one mode or the other.
const hasNamedVariables = (text) => extractVarTokens(text).some((t) => !/^\d+$/.test(t));

const buildComponents = (form, tagMap = {}) => {
  const components = [];

  if (form.headerFormat === 'IMAGE' && form.headerText.trim()) {
    components.push({ type: 'HEADER', format: 'IMAGE', example: { header_handle: [form.headerText.trim()] } });
  } else if (form.headerText.trim()) {
    const headerText = fixLeadingTrailingVars(form.headerText.trim());
    const headerVarCount = countVariables(headerText);
    const headerComp = { type: 'HEADER', format: 'TEXT', text: headerText };
    if (headerVarCount > 0) {
      const headerExampleValues = _exampleValuesForText(headerText, tagMap);
      if (hasNamedVariables(headerText)) {
        // Named-parameter form: each entry is {example, param_name}.
        headerComp.example = {
          header_text_named_params: headerExampleValues.map((v) => ({
            example: v,
            param_name: v,
          })),
        };
      } else {
        // Positional form: flat array.
        headerComp.example = { header_text: headerExampleValues };
      }
    }
    components.push(headerComp);
  }

  const bodyText = fixLeadingTrailingVars(form.bodyText.trim());
  const bodyVarCount = countVariables(bodyText);
  const bodyComp = { type: 'BODY', text: bodyText };
  if (bodyVarCount > 0) {
    const bodyExampleValues = _exampleValuesForText(bodyText, tagMap);
    if (hasNamedVariables(bodyText)) {
      // Named-parameter form: list of {example, param_name} dicts.
      // Meta rejects (code 100) any BODY containing `{{name}}` without this.
      bodyComp.example = {
        body_text_named_params: bodyExampleValues.map((v) => ({
          example: v,
          param_name: v,
        })),
      };
    } else {
      // Positional form: nested array `[["val1", "val2"]]`.
      bodyComp.example = { body_text: [bodyExampleValues] };
    }
  }
  components.push(bodyComp);

  if (form.footerText.trim()) {
    components.push({ type: 'FOOTER', text: form.footerText.trim() });
  }

  if (form.buttonType === 'form_link' && form.buttonLabel.trim()) {
    // FORMS-only preset: dynamic-suffix URL button. The backend's
    // ``generate_template_context_mapping`` reads ``button.example`` and
    // emits a ``button`` component at send time substituting
    // ``form_url_token`` (the access token only — Meta doesn't allow
    // variables in the protocol+domain prefix).
    const formsBaseUrl = form.formsBaseUrl || 'https://forms.example.com';
    components.push({
      type: 'BUTTONS',
      buttons: [{
        type: 'URL',
        text: form.buttonLabel.trim(),
        url: `${formsBaseUrl.replace(/\/$/, '')}/consent/{{1}}`,
        example: ['form_url_token'],
      }],
    });
  } else if (form.buttonType === 'url' && form.buttonLabel.trim()) {
    components.push({
      type: 'BUTTONS',
      buttons: [{ type: 'URL', text: form.buttonLabel.trim(), url: form.buttonUrl || 'https://{{1}}' }],
    });
  } else if (form.buttonType === 'phone' && form.buttonLabel.trim()) {
    components.push({
      type: 'BUTTONS',
      buttons: [{ type: 'PHONE_NUMBER', text: form.buttonLabel.trim(), phone_number: form.buttonPhone || '+441234567890' }],
    });
  }

  return components;
};

const getProgressColor = (pct, over) => {
  if (over) return 'error';
  if (pct > 80) return 'warning';
  return 'primary';
};

const CharCount = ({ current, max }) => {
  const pct = (current / max) * 100;
  const over = current > max;
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
      <LinearProgress
        variant="determinate"
        value={Math.min(pct, 100)}
        color={getProgressColor(pct, over)}
        sx={{ flex: 1, height: 2, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.04)' }}
      />
      <Typography
        sx={{
          minWidth: 44,
          textAlign: 'right',
          fontSize: '0.625rem',
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
          color: over ? 'error.main' : 'text.disabled',
          letterSpacing: '0.02em',
        }}
      >
        {current}/{max}
      </Typography>
    </Stack>
  );
};

CharCount.propTypes = {
  current: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
};

const getNameHelperText = (isDuplicate, name, isEdit) => {
  if (isDuplicate && name) return 'A template with this name already exists';
  if (isEdit) return 'Template name cannot be changed after creation';
  return undefined;
};

const getNameHelperProps = (isDuplicate, name, isEdit) => {
  if (isDuplicate && name) return { sx: { fontSize: '0.68rem', color: 'error.main', ml: 0.5, mt: 0.5 } };
  if (isEdit) return { sx: { fontSize: '0.68rem', color: 'text.disabled', ml: 0.5, mt: 0.5 } };
  return undefined;
};

// ─── Greeting-name heuristic ─────────────────────────────────────────────────
// Detect a literal first name in a greeting position, e.g. "Hi Sarah," or
// "Hello James,". Common non-name words are allow-listed so we don't warn on
// "Hi There," or "Dear Valued,". Practice staff names in sign-offs ("Regards,
// Daniela") do NOT match because they appear at the end of a line, not in a
// greeting opener.
const GREETING_COMMON_WORDS = new Set([
  'There', 'All', 'Team', 'Everyone', 'Valued', 'Dear', 'Patients',
]);
const GREETING_NAME_RE = /(?:^|\n)(?:Hi|Hello|Hey|Dear)\s+([A-Z][a-z]{2,14}),/;

// ─── Main Dialog ─────────────────────────────────────────────────────────────
const RequestTemplateDialog = ({
  open, onClose, onSubmit, submitting, editTemplate, existingTemplateNames, cloneData,
  // Audience rules. Saved INDEPENDENTLY of the main submit: audience is CRM-side
  // metadata that Meta neither receives nor cares about, so folding it into
  // "Save & Submit For Approval" would push the template for re-approval and
  // burn the once-per-24h edit allowance for a change Meta never sees.
  // Omitted by callers that cannot persist it — the section then does not render.
  onUpdateAudience = null,
  practiceTreatments = [],
}) => {
  const isEditMode = !!editTemplate;
  const [form, setForm] = useState(INITIAL_FORM);
  const [activeField, setActiveField] = useState('bodyText');
  // Track which {{N}} maps to which merge tag for preview
  const [tagMap, setTagMap] = useState({});
  const [testValues, setTestValues] = useState({});

  // ── Audience rules (independent of the Meta submit) ──────────────────
  const { canView: canViewAudience, canManage: canManageAudience } = useAudiencePermission();
  const [audienceDraft, setAudienceDraft] = useState(() => toRuleValue(editTemplate));
  const [audienceSaving, setAudienceSaving] = useState(false);
  const [audienceSaved, setAudienceSaved] = useState(false);
  // Local baseline for the dirty check. Deliberately NOT `editTemplate` — the
  // parent may hand back a stale object after a refresh (or not refresh this
  // object at all), which would leave the Save button stuck on screen after a
  // save that actually succeeded.
  const [audienceBaseline, setAudienceBaseline] = useState(() => toRuleValue(editTemplate));

  useEffect(() => {
    setAudienceDraft(toRuleValue(editTemplate));
    setAudienceBaseline(toRuleValue(editTemplate));
    setAudienceSaved(false);
  }, [editTemplate?.id, open]);

  // Only offered when EDITING a persisted template: a template being created
  // has no id yet, so there is nothing to PATCH. Audience for a brand-new
  // template is set from the templates grid once Meta approves it — which is
  // also the first moment it could be sent.
  const showAudienceSection = Boolean(
    canViewAudience && isEditMode && editTemplate?.id && onUpdateAudience,
  );

  const audienceDirty = useMemo(
    () => JSON.stringify(toPayload(audienceDraft)) !== JSON.stringify(toPayload(audienceBaseline)),
    [audienceDraft, audienceBaseline],
  );

  const handleSaveAudience = async () => {
    if (!onUpdateAudience || validateRule(audienceDraft) || audienceSaving) return;
    setAudienceSaving(true);
    try {
      // Callers return false on failure; only move the baseline (and so clear
      // the dirty state) when the write actually landed.
      const ok = await onUpdateAudience(toPayload(audienceDraft), editTemplate?.id);
      if (ok !== false) {
        setAudienceBaseline(toRuleValue({ ...editTemplate, ...toPayload(audienceDraft) }));
        setAudienceSaved(true);
      }
    } finally {
      setAudienceSaving(false);
    }
  };
  const bodyRef = useRef(null);
  const headerRef = useRef(null);
  // Progressive disclosure — each optional section shows only if it has
  // content OR the user explicitly adds it. No Basic/Advanced mode toggle;
  // the form just shows the fields that are actually in use. When editing an
  // existing template these default to "expanded" for whichever fields have
  // real content, so the user never loses sight of their own data.
  const [showHeader, setShowHeader] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [showCta, setShowCta] = useState(false);
  // Category + Usage render as compact chips at the top of the form; each
  // chip opens a Menu via the anchor stored here.
  const [categoryAnchor, setCategoryAnchor] = useState(null);
  const [usageAnchor, setUsageAnchor] = useState(null);
  // Refs used to auto-focus the first field of an optional section the
  // moment the user reveals it, so typing can begin immediately without a
  // second click.
  const headerInputRef = useRef(null);
  const footerInputRef = useRef(null);
  // Edit-restriction notice in the header. Collapsed by default — one short
  // sentence visible; clicking "Read more" expands the full explanation.
  const [showEditRestrictionDetail, setShowEditRestrictionDetail] = useState(false);
  // "Test with real data" block is quiet by default — most users are just
  // composing and reading the preview. Expand on demand when they want to
  // sanity-check a specific substitution.
  const [showTestValues, setShowTestValues] = useState(false);
  // Inline formatting toolbar (bold/italic/etc) is rarely needed. Keep it
  // hidden behind a small "T" toggle on the body field.
  const [showFormattingToolbar, setShowFormattingToolbar] = useState(false);
  // Non-null when the heuristic detects a likely hardcoded first name in a
  // greeting position. Set to the detected word; null when clear.
  const [hardcodedNameWarning, setHardcodedNameWarning] = useState(null);

  // Shared style for the inline "Add …" / "Category & usage" pill buttons
  // that appear below the form when optional sections are collapsed.
  const addPillSx = {
    textTransform: 'none',
    fontSize: '0.72rem',
    fontWeight: 500,
    color: 'text.secondary',
    bgcolor: 'rgba(0,0,0,0.04)',
    borderRadius: '999px',
    px: 1.25,
    py: 0.25,
    '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
  };

  // Pull real practice data for preview examples
  const { practice } = useSelector((state) => state.practice);
  const [practiceBookingUrl, setPracticeBookingUrl] = useState('');

  // Fetch active booking URLs from the treatments API
  useEffect(() => {
    if (!open) return;
    const practiceId = getPracticeId();
    if (!practiceId) return;

    (async () => {
      const res = await callApi(
        `/api/v1/practices/${practiceId}/treatments/?booking_urls__is_active=true&limit=100`
      );
      if (res?.ok && res.data?.results) {
        // Find the first active booking URL across all treatments
        const allUrls = res.data.results.flatMap((t) => t.booking_urls || []);
        const activeUrl = allUrls.find((u) => u.is_active && u.booking_url);
        if (activeUrl) setPracticeBookingUrl(activeUrl.booking_url);
      }
    })();
  }, [open]);

  // Pre-fill form when editing an existing template or cloning
  useEffect(() => {
    if (!open) return;
    if (editTemplate) {
      const header = editTemplate.components?.find(c => c.type === 'HEADER');
      const body = editTemplate.components?.find(c => c.type === 'BODY');
      const footer = editTemplate.components?.find(c => c.type === 'FOOTER');
      const buttons = editTemplate.components?.find(c => c.type === 'BUTTONS');
      const btn = buttons?.buttons?.[0];

      const displayName = (editTemplate.name || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());

      let buttonType = 'none';
      let buttonLabel = 'Book Now';
      let buttonUrl = '';
      let buttonPhone = '';
      if (btn?.type === 'URL') {
        buttonType = 'url';
        buttonLabel = btn.text || 'Book Now';
        buttonUrl = btn.url || '';
      } else if (btn?.type === 'PHONE_NUMBER') {
        buttonType = 'phone';
        buttonLabel = btn.text || 'Call Us';
        buttonPhone = btn.phone_number || '';
      }

      setForm({
        displayName,
        headerFormat: header?.format || 'TEXT',
        headerText: header?.format === 'IMAGE' ? (header?.example?.header_handle?.[0] || '') : (header?.text || ''),
        bodyText: body?.text || '',
        footerText: footer?.text || '',
        buttonType,
        buttonLabel,
        buttonUrl,
        buttonPhone,
        usageType: editTemplate.usage_type || 'FOLLOW_UP',
        category: editTemplate.category || 'UTILITY',
      });
      setTagMap({});
      setTestValues({});
      setHardcodedNameWarning(null);
      // Reveal each optional section only if the template actually uses it.
      setShowHeader(!!(header?.text || header?.format === 'IMAGE'));
      setShowFooter(!!footer?.text);
      setShowCta(!!buttons?.buttons?.length);
    } else if (cloneData) {
      setForm(cloneData);
      setTagMap({});
      setTestValues({});
      setHardcodedNameWarning(null);
    }
  }, [open, editTemplate, cloneData]);

  const practiceExamples = useMemo(() => getPracticeExamples(practice, practiceBookingUrl), [practice, practiceBookingUrl]);
  const isFormsTemplate = form.usageType === 'FORMS';
  const mergeTags = useMemo(
    () => buildMergeTags(practiceExamples, { isFormsTemplate }),
    [practiceExamples, isFormsTemplate]
  );

  const templateName = useMemo(() => toTemplateName(form.displayName), [form.displayName]);
  const isDuplicateName = !isEditMode && existingTemplateNames.includes(templateName);
  const totalVarCount = useMemo(
    () => countVariables(form.headerText) + countVariables(form.bodyText),
    [form.headerText, form.bodyText]
  );

  // Build a template object for the WhatsAppTemplatePreviewPanel
  const previewTemplate = useMemo(() => {
    const hasContent = form.bodyText.trim() || form.headerText.trim();
    if (!hasContent) return null;

    const components = [];
    if (form.headerFormat === 'IMAGE' && form.headerText.trim()) {
      components.push({ type: 'HEADER', format: 'IMAGE', example: { header_handle: [form.headerText.trim()] } });
    } else if (form.headerText.trim()) {
      components.push({ type: 'HEADER', format: 'TEXT', text: form.headerText.trim() });
    }
    if (form.bodyText.trim()) {
      // Apply the same leading/trailing-var fix the BE applies so the preview
      // shows the final approved text (e.g. trailing "." added after a var).
      components.push({ type: 'BODY', text: fixLeadingTrailingVars(form.bodyText.trim()) });
    }
    if (form.footerText.trim()) {
      components.push({ type: 'FOOTER', text: form.footerText.trim() });
    }
    if (form.buttonType === 'form_link' && form.buttonLabel.trim()) {
      components.push({
        type: 'BUTTONS',
        buttons: [{
          type: 'URL',
          text: form.buttonLabel.trim(),
          url: FORM_LINK_URL_PATTERN(practiceExamples.formsBaseUrl),
          example: [FORM_LINK_EXAMPLE_TOKEN],
        }],
      });
    } else if (form.buttonType === 'url' && form.buttonLabel.trim()) {
      components.push({
        type: 'BUTTONS',
        buttons: [{ type: 'URL', text: form.buttonLabel.trim(), url: form.buttonUrl || 'https://book.example.com' }],
      });
    } else if (form.buttonType === 'phone' && form.buttonLabel.trim()) {
      components.push({
        type: 'BUTTONS',
        buttons: [{ type: 'PHONE_NUMBER', text: form.buttonLabel.trim(), phone_number: form.buttonPhone || practiceExamples.phone }],
      });
    }

    return {
      name: templateName || 'new_template',
      usage_type: form.usageType || 'FOLLOW_UP',
      components,
      template_context_mapping: null,
    };
  }, [form.headerFormat, form.headerText, form.bodyText, form.footerText, form.buttonType, form.buttonLabel, form.buttonUrl, form.buttonPhone, form.usageType, templateName, practiceExamples]);

  // Build context mapping from tagMap (var number → example value for preview).
  // For variables not in tagMap (e.g. typed manually), fall back to the
  // default merge tag order so the preview always shows real practice data.
  const previewContextMapping = useMemo(() => {
    const mapping = {};
    const allText = `${form.headerText} ${form.bodyText}`;
    const nums = (allText.match(/\{\{(\d+)\}\}/g) || [])
      .map((m) => parseInt(m.replace(/\{|\}/g, ''), 10))
      .sort((a, b) => a - b);

    nums.forEach((num, idx) => {
      if (testValues[num]) {
        mapping[num] = testValues[num];
      } else if (tagMap[num]) {
        mapping[num] = tagMap[num].example;
      } else {
        const fallbackTag = mergeTags[idx];
        mapping[num] = fallbackTag ? fallbackTag.example : `var${num}`;
      }
    });
    return mapping;
  }, [tagMap, testValues, form.headerText, form.bodyText, mergeTags]);

  const handleChange = useCallback((field) => (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'bodyText') {
      const match = GREETING_NAME_RE.exec(value);
      if (match && !GREETING_COMMON_WORDS.has(match[1])) {
        setHardcodedNameWarning(match[1]);
      } else {
        setHardcodedNameWarning(null);
      }
    }
  }, []);

  // One-click replace: swaps the detected hardcoded name with a First Name
  // variable ({{N}}) using the same insertMergeTag positional system.
  const handleReplaceHardcodedName = () => {
    if (!hardcodedNameWarning) return;
    const tag = mergeTags[0]; // always first_name
    setForm((prev) => {
      const nextNum = getNextVarNumber(prev);
      const insertion = `{{${nextNum}}}`;
      setTagMap((t) => ({ ...t, [nextNum]: tag }));
      return { ...prev, bodyText: prev.bodyText.replace(hardcodedNameWarning, insertion) };
    });
    setHardcodedNameWarning(null);
  };

  const insertMergeTag = useCallback((tag) => {
    const field = activeField;
    const ref = field === 'headerText' ? headerRef : bodyRef;
    const el = ref.current?.querySelector?.('textarea, input');

    setForm((prev) => {
      const nextNum = getNextVarNumber(prev);
      const insertion = `{{${nextNum}}}`;

      setTagMap((t) => ({ ...t, [nextNum]: tag }));

      const currentText = prev[field];
      if (el) {
        const start = el.selectionStart || currentText.length;
        const end = el.selectionEnd || currentText.length;
        const before = currentText.slice(0, start);
        const after = currentText.slice(end);
        const spaceBefore = before.length > 0 && !before.endsWith(' ') && !before.endsWith('\n') ? ' ' : '';
        const spaceAfter = after.length > 0 && !after.startsWith(' ') && !after.startsWith('\n') ? ' ' : '';
        return { ...prev, [field]: `${before}${spaceBefore}${insertion}${spaceAfter}${after}` };
      }
      return { ...prev, [field]: `${currentText} ${insertion}` };
    });
  }, [activeField]);

  const insertFormatting = useCallback((prefix, suffix) => {
    const el = bodyRef.current?.querySelector?.('textarea');
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const text = form.bodyText;
    const selected = text.slice(start, end);
    const replacement = selected ? `${prefix}${selected}${suffix}` : `${prefix}text${suffix}`;
    const newText = text.slice(0, start) + replacement + text.slice(end);
    setForm((prev) => ({ ...prev, bodyText: newText }));
    // Restore cursor position after the insertion
    setTimeout(() => {
      const newPos = selected ? start + replacement.length : start + prefix.length + 4; // "text" = 4 chars
      el.focus();
      el.setSelectionRange(newPos, newPos);
    }, 0);
  }, [form.bodyText]);

  const handleSubmit = async () => {
    // Flush a dirty audience draft FIRST. The main payload deliberately does
    // not carry audience — that submit goes to Meta, and audience is CRM-side
    // metadata Meta never sees — but every caller closes this dialog once
    // onSubmit fires. Without this, an operator who edits the audience and then
    // presses the primary Save button watches their change vanish, discarded by
    // the very action they took to keep it.
    if (showAudienceSection && canManageAudience && audienceDirty
        && !validateRule(audienceDraft)) {
      await handleSaveAudience();
    }

    // Inject the practice-level forms base URL so the form_link CTA
    // preset can build its static prefix. Backend resolves the real base
    // from the practice's ``consent_form_config`` at send time; this is
    // only what's stamped onto the Meta-approved template.
    const formForSubmit = { ...form, formsBaseUrl: practiceExamples.formsBaseUrl };
    const payload = {
      template: {
        name: templateName,
        language: DEFAULT_LANGUAGE,
        // Forward the chosen usage_type so the BE persists it post-create
        // (``create_from_meta`` view stamps it after the immediate sync —
        // sync_templates defaults to OTHER because Meta doesn't track
        // usage_type, so we have to stamp it locally).
        usage_type: form.usageType || 'FOLLOW_UP',
      },
      // Pass ``tagMap`` so chip keys land in ``example.body_text`` / etc.,
      // which is what the backend's ``generate_template_context_mapping``
      // reads to build the runtime substitution mapping.
      components: buildComponents(formForSubmit, tagMap),
    };
    payload.template.category = form.category;
    // When editing, include the template ID so the caller can use the update endpoint
    if (isEditMode && editTemplate?.id) {
      payload.templateId = editTemplate.id;
      payload.isEdit = true;
    }
    if (onSubmit) onSubmit(payload);
  };

  const handleClose = useCallback(() => {
    if (submitting) return;
    setForm(INITIAL_FORM);
    setTagMap({});
    setTestValues({});
    setHardcodedNameWarning(null);
    setPracticeBookingUrl('');
    onClose();
  }, [submitting, onClose]);

  const headerOver = form.headerText.length > LIMITS.HEADER;
  const bodyOver = form.bodyText.length > LIMITS.BODY;
  const footerOver = form.footerText.length > LIMITS.FOOTER;

  const isValid =
    templateName.length > 0 &&
    form.bodyText.trim().length > 0 &&
    !headerOver &&
    !bodyOver &&
    !footerOver &&
    !isDuplicateName;

  // Live reasons why the submit button is disabled — shown near the button
  // so the user knows exactly what to fix without having to guess.
  const validationHints = [];
  if (!isValid) {
    if (!templateName.length) validationHints.push('Add a template name');
    if (!form.bodyText.trim().length) validationHints.push('Add a message body');
    if (isDuplicateName) validationHints.push('A template with this name already exists');
    const headerOver_ = form.headerText.length - LIMITS.HEADER;
    if (headerOver) validationHints.push(`Header is ${headerOver_} character${headerOver_ !== 1 ? 's' : ''} over the limit`);
    const bodyOver_ = form.bodyText.length - LIMITS.BODY;
    if (bodyOver) validationHints.push(`Message is ${bodyOver_} character${bodyOver_ !== 1 ? 's' : ''} over the limit`);
    const footerOver_ = form.footerText.length - LIMITS.FOOTER;
    if (footerOver) validationHints.push(`Footer is ${footerOver_} character${footerOver_ !== 1 ? 's' : ''} over the limit`);
  }

  // Detect trailing / leading variables so we can show a transparency note
  // (the BE's fixLeadingTrailingVars appends "." / prepends "Hi " — mirror
  // that in the preview and explain it inline).
  const bodyTrimmed = form.bodyText.trim();
  const hasTrailingVar = bodyTrimmed.length > 0 &&
    /\{\{(?:\d+|[a-zA-Z_][a-zA-Z0-9_]*)\}\}\s*$/.test(bodyTrimmed);
  const hasLeadingVar = bodyTrimmed.length > 0 &&
    /^\s*\{\{(?:\d+|[a-zA-Z_][a-zA-Z0-9_]*)\}\}/.test(bodyTrimmed);

  // Button label for submit action
  let submitButtonLabel = 'Create WhatsApp Template';
  if (submitting && isEditMode) submitButtonLabel = 'Saving...';
  else if (submitting) submitButtonLabel = 'Creating...';
  else if (isEditMode) submitButtonLabel = 'Save & Submit for Approval';

  // Shared sx for Apple-style text fields
  const appleInputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      bgcolor: '#f5f5f7',
      fontSize: '0.84rem',
      transition: 'all 0.2s ease',
      '& fieldset': { borderColor: 'rgba(0,0,0,0.06)' },
      '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
      '&.Mui-focused fieldset': { borderColor: '#00AB55', borderWidth: '1.5px' },
      '&.Mui-focused': { bgcolor: '#fff', boxShadow: '0 0 0 3px rgba(0,171,85,0.1)' },
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.8rem',
      fontWeight: 500,
      letterSpacing: '0.01em',
      '&.Mui-focused': { color: '#00AB55' },
    },
  };

  // Shared sx for uppercase section labels (iOS style)
  const sectionLabelSx = {
    fontSize: '0.65rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: 'text.secondary',
    mb: 0.75,
    display: 'block',
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '90vh',
          border: '0.5px solid rgba(0,0,0,0.06)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        },
      }}
    >
      {/* ─── Header ─── */}
      <Box sx={{ borderBottom: '0.5px solid rgba(0,0,0,0.06)' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 1.5 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: '#25D366',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              <Iconify icon="mdi:whatsapp" width={18} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, letterSpacing: '-0.01em' }}>
                {isEditMode ? 'Edit WhatsApp Template' : 'Create WhatsApp Template'}
              </Typography>
              {isEditMode && (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                  <Iconify icon="mdi:clock-outline" width={12} sx={{ color: 'text.disabled' }} />
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                    Editable once every 24 hours.
                  </Typography>
                  <Button
                    size="small"
                    disableRipple
                    onClick={() => setShowEditRestrictionDetail((v) => !v)}
                    sx={{
                      minWidth: 0,
                      p: 0,
                      ml: 0.25,
                      textTransform: 'none',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      color: '#00AB55',
                      '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                    }}
                  >
                    {showEditRestrictionDetail ? 'Hide' : 'Read more'}
                  </Button>
                </Stack>
              )}
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={handleClose}
            disabled={submitting}
            sx={{
              width: 26,
              height: 26,
              bgcolor: 'rgba(0,0,0,0.04)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
            }}
          >
            <Iconify icon="mdi:close" width={14} sx={{ color: 'text.disabled' }} />
          </IconButton>
        </Stack>
        {isEditMode && showEditRestrictionDetail && (
          <Box
            sx={{
              px: 2.5,
              pb: 1.5,
              pt: 0.25,
              fontSize: '0.72rem',
              color: 'text.secondary',
              lineHeight: 1.45,
            }}
          >
            After saving, this template will be sent to Meta for re-approval and may be temporarily unavailable to send. If the edit is rejected, the previous approved version stays live.
          </Box>
        )}
        {/* ─── Meta quality / pause banner ───
            Surfaces when Meta has paused the template, or when quality has
            dropped to RED. Sits inside the header so it stays sticky as
            DialogContent scrolls. Mirrors the 24h notice aesthetic. */}
        {isEditMode && (() => {
          const status = String(editTemplate?.status || '').toUpperCase();
          const quality = String(editTemplate?.quality_score || '').toUpperCase();
          const isPaused = status === 'PAUSED';
          const isQualityRed = quality === 'RED';
          if (!isPaused && !isQualityRed) return null;

          const reason = editTemplate?.status_reason || '';
          const updated = editTemplate?.status_updated_at
            ? new Date(editTemplate.status_updated_at).toLocaleString()
            : null;

          const headline = isPaused
            ? 'Meta has paused this template'
            : 'Template quality has dropped to RED';
          const explainer = isPaused
            ? 'Sends are blocked until Meta lifts the pause. Edit the content (especially anything users may have flagged) and re-submit so Meta can re-review.'
            : 'Meta is heavily throttling this template due to user feedback. Replace flagged content or clone this template into a new one to recover delivery.';

          return (
            <Box
              sx={{
                mx: 2,
                mb: 1.5,
                px: 1.5,
                py: 1,
                bgcolor: isPaused ? 'warning.lighter' : 'error.lighter',
                border: '0.5px solid',
                borderColor: isPaused ? 'warning.light' : 'error.light',
                borderRadius: '10px',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Iconify
                  icon={isPaused ? 'mdi:pause-circle-outline' : 'mdi:alert-circle-outline'}
                  width={18}
                  sx={{ color: isPaused ? 'warning.dark' : 'error.dark', mt: '1px', flexShrink: 0 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      color: isPaused ? 'warning.darker' : 'error.darker',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {headline}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.25, lineHeight: 1.45 }}>
                    {explainer}
                  </Typography>
                  {reason && (
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: 'text.secondary',
                        mt: 0.5,
                        fontStyle: 'italic',
                      }}
                    >
                      Meta said: {reason}
                    </Typography>
                  )}
                  {updated && (
                    <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', mt: 0.5 }}>
                      Last update: {updated}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          );
        })()}
      </Box>

      <DialogContent sx={{ p: 2.5, pt: 2.5 }}>
        <Stack direction="row" spacing={2.5}>
          {/* ─── Left: Form ─── */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={2}>
              {/* Template Name
                  In create mode this is an editable field. In edit mode Meta
                  forbids name changes, so we render it as a read-only summary
                  instead of a greyed-out disabled input — cleaner visually
                  and makes the "you can't change this" rule obvious without
                  an error-style affordance. */}
              {isEditMode ? (
                <Box
                  sx={{
                    bgcolor: '#f5f5f7',
                    borderRadius: '10px',
                    border: '0.5px solid rgba(0,0,0,0.06)',
                    px: 1.5,
                    py: 1.25,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          color: 'text.disabled',
                          mb: 0.25,
                        }}
                      >
                        Template Name
                      </Typography>
                      <Typography
                        noWrap
                        title={form.displayName}
                        sx={{ fontSize: '0.9rem', fontWeight: 600, letterSpacing: '-0.01em', color: 'text.primary' }}
                      >
                        {form.displayName}
                      </Typography>
                    </Box>
                    <Tooltip title="Template names are set at creation and cannot be changed. Clone this template to create a new one with a different name.">
                      <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: 'text.disabled', flexShrink: 0 }}>
                        <Iconify icon="mdi:lock-outline" width={12} />
                        <Typography sx={{ fontSize: '0.68rem' }}>Locked</Typography>
                      </Stack>
                    </Tooltip>
                  </Stack>
                </Box>
              ) : (
                <Box>
                  <Typography sx={sectionLabelSx}>Template Name</Typography>
                  <TextField
                    value={form.displayName}
                    onChange={handleChange('displayName')}
                    placeholder="e.g. Appointment Reminder"
                    fullWidth
                    size="small"
                    error={isDuplicateName && !!templateName}
                    sx={appleInputSx}
                    helperText={getNameHelperText(isDuplicateName, templateName, isEditMode)}
                    FormHelperTextProps={getNameHelperProps(isDuplicateName, templateName, isEditMode)}
                  />
                </Box>
              )}

              {/* Category + Usage chips. Always visible, always editable —
                  click opens a Menu with the available options. Replaces the
                  old full-width Select blocks buried behind an Advanced
                  collapse; these values are part of a template's identity
                  (and shown as status chips in the list), so they belong at
                  the top of the form, not hidden. */}
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: -0.5 }}>
                {(() => {
                  const cat = META_CATEGORIES.find((c) => c.value === form.category) || META_CATEGORIES[0];
                  const usage = ALLOWED_USAGE_TYPES.find((u) => u.value === form.usageType) || ALLOWED_USAGE_TYPES[0];
                  return (
                    <>
                      <Button
                        size="small"
                        onClick={(e) => setCategoryAnchor(e.currentTarget)}
                        startIcon={<Iconify icon={cat.icon} width={14} />}
                        endIcon={<Iconify icon="mdi:chevron-down" width={14} />}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.72rem',
                          fontWeight: 500,
                          color: '#00AB55',
                          bgcolor: 'rgba(0,171,85,0.08)',
                          border: '0.5px solid rgba(0,171,85,0.2)',
                          borderRadius: '999px',
                          px: 1.25,
                          py: 0.25,
                          '&:hover': { bgcolor: 'rgba(0,171,85,0.14)' },
                        }}
                      >
                        {cat.label}
                      </Button>
                      <Button
                        size="small"
                        onClick={(e) => setUsageAnchor(e.currentTarget)}
                        startIcon={<Iconify icon={usage.icon} width={14} />}
                        endIcon={<Iconify icon="mdi:chevron-down" width={14} />}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.72rem',
                          fontWeight: 500,
                          color: 'text.secondary',
                          bgcolor: 'rgba(0,0,0,0.04)',
                          border: '0.5px solid rgba(0,0,0,0.08)',
                          borderRadius: '999px',
                          px: 1.25,
                          py: 0.25,
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                        }}
                      >
                        {usage.label}
                      </Button>
                    </>
                  );
                })()}
              </Stack>
              <Menu
                anchorEl={categoryAnchor}
                open={!!categoryAnchor}
                onClose={() => setCategoryAnchor(null)}
                PaperProps={{ sx: { borderRadius: '12px', mt: 0.5 } }}
              >
                {META_CATEGORIES.map((mc) => (
                  <MenuItem
                    key={mc.value}
                    onClick={() => { setForm((prev) => ({ ...prev, category: mc.value })); setCategoryAnchor(null); }}
                    selected={form.category === mc.value}
                    sx={{ fontSize: '0.82rem', gap: 1, borderRadius: '6px', mx: 0.5 }}
                  >
                    <Iconify icon={mc.icon} width={16} sx={{ color: 'text.secondary' }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{mc.label}</Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>{mc.desc}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
              <Menu
                anchorEl={usageAnchor}
                open={!!usageAnchor}
                onClose={() => setUsageAnchor(null)}
                PaperProps={{ sx: { borderRadius: '12px', mt: 0.5 } }}
              >
                {ALLOWED_USAGE_TYPES.map((ut) => (
                  <MenuItem
                    key={ut.value}
                    onClick={() => { setForm((prev) => ({ ...prev, usageType: ut.value })); setUsageAnchor(null); }}
                    selected={form.usageType === ut.value}
                    sx={{ fontSize: '0.82rem', gap: 1, borderRadius: '6px', mx: 0.5 }}
                  >
                    <Iconify icon={ut.icon} width={16} sx={{ color: 'text.secondary' }} />
                    {ut.label}
                  </MenuItem>
                ))}
              </Menu>

              {/* FORMS-mode hint banner. Shown only when the admin has
                  picked ``usage_type=FORMS`` so the chip rail and CTA
                  preset options change. Keeps the rest of the dialog
                  unchanged for non-FORMS templates. */}
              {isFormsTemplate && (
                <Box
                  sx={{
                    bgcolor: 'rgba(0, 171, 85, 0.08)',
                    border: '0.5px solid rgba(0, 171, 85, 0.2)',
                    borderRadius: '10px',
                    p: 1.25,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Iconify
                    icon="mdi:file-document-edit-outline"
                    width={18}
                    sx={{ color: '#00AB55', mt: 0.1 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.primary', mb: 0.25 }}>
                      Forms template
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1.45 }}>
                      The Send Forms wizard picks this template to deliver consent links. Use the{' '}
                      <Box component="span" sx={{ fontWeight: 600 }}>Form Link</Box> chip in the body, or pick the{' '}
                      <Box component="span" sx={{ fontWeight: 600 }}>Send Form</Box> button under Call-to-action — each patient gets a unique URL.
                    </Typography>
                  </Box>
                </Box>
              )}

              {showHeader && (
              <Box ref={headerRef}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                  <Typography sx={{ ...sectionLabelSx, mb: 0 }}>
                    Header
                    <Box component="span" sx={{ textTransform: 'none', fontWeight: 400, color: 'text.disabled', ml: 0.5 }}>
                      — optional
                    </Box>
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    {HEADER_FORMATS.map((hf) => (
                      <Chip
                        key={hf.value}
                        label={hf.label}
                        size="small"
                        icon={<Iconify icon={hf.icon} width={13} />}
                        onClick={() => setForm((prev) => ({ ...prev, headerFormat: hf.value, headerText: '' }))}
                        variant={form.headerFormat === hf.value ? 'filled' : 'outlined'}
                        sx={{
                          height: 24,
                          fontSize: '0.65rem',
                          fontWeight: 500,
                          borderRadius: '6px',
                          cursor: 'pointer',
                          ...(form.headerFormat === hf.value
                            ? { bgcolor: '#00AB55', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }
                            : { borderColor: 'rgba(0,0,0,0.08)', color: 'text.secondary', '& .MuiChip-icon': { color: 'inherit' } }),
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>
                {form.headerFormat === 'TEXT' ? (
                  <>
                    <TextField
                      value={form.headerText}
                      onChange={handleChange('headerText')}
                      onFocus={() => setActiveField('headerText')}
                      placeholder="e.g. Appointment Confirmation"
                      error={headerOver}
                      fullWidth
                      size="small"
                      sx={appleInputSx}
                      inputRef={headerInputRef}
                    />
                    {form.headerText.length > 0 && <CharCount current={form.headerText.length} max={LIMITS.HEADER} />}
                  </>
                ) : (
                  <TextField
                    value={form.headerText}
                    onChange={handleChange('headerText')}
                    placeholder="https://example.com/image.jpg"
                    fullWidth
                    size="small"
                    sx={appleInputSx}
                    helperText="Enter a URL to a JPG or PNG image (min 600x300px)"
                    FormHelperTextProps={{ sx: { fontSize: '0.62rem', color: 'text.disabled', ml: 0.5, mt: 0.5 } }}
                    inputRef={headerInputRef}
                  />
                )}
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    onClick={() => { setShowHeader(false); setForm((prev) => ({ ...prev, headerText: '', headerFormat: 'TEXT' })); }}
                    startIcon={<Iconify icon="mdi:close" width={14} />}
                    sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'text.secondary', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                  >
                    Remove header
                  </Button>
                </Box>
              </Box>
              )}

              {/* Body */}
              <Box ref={bodyRef}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography sx={{ ...sectionLabelSx, mb: 0 }}>Message</Typography>
                  <Tooltip title={showFormattingToolbar ? 'Hide formatting' : 'Show formatting options'}>
                    <IconButton
                      size="small"
                      onClick={() => setShowFormattingToolbar((v) => !v)}
                      sx={{
                        width: 24, height: 24, borderRadius: '6px',
                        color: showFormattingToolbar ? '#00AB55' : 'text.disabled',
                        bgcolor: showFormattingToolbar ? 'rgba(0,171,85,0.08)' : 'transparent',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                      }}
                    >
                      <Iconify icon="mdi:format-letter-case" width={14} />
                    </IconButton>
                  </Tooltip>
                </Stack>

                {/* Personalisation chips — above the textarea so users see them before typing */}
                <Box sx={{ mb: 1 }}>
                  <Typography sx={sectionLabelSx}>
                    Personalisation
                    {totalVarCount > 0 && (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.75,
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          color: '#00AB55',
                          bgcolor: 'rgba(0,171,85,0.08)',
                          px: 0.75,
                          py: 0.15,
                          borderRadius: '10px',
                          verticalAlign: 'middle',
                        }}
                      >
                        {totalVarCount}
                      </Box>
                    )}
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {mergeTags.map((tag) => (
                      <Tooltip key={tag.key} title={tag.example ? `e.g. "${tag.example}"` : ''}>
                        <Chip
                          label={tag.label}
                          size="small"
                          icon={<Iconify icon={tag.icon} width={13} />}
                          onClick={() => insertMergeTag(tag)}
                          clickable
                          variant="outlined"
                          sx={{
                            borderRadius: '8px',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            height: 28,
                            borderColor: 'rgba(0,0,0,0.08)',
                            color: 'text.secondary',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              bgcolor: 'rgba(0,171,85,0.06)',
                              borderColor: 'rgba(0,171,85,0.25)',
                              color: '#00AB55',
                            },
                            '&:active': { transform: 'scale(0.97)' },
                            '& .MuiChip-icon': { color: 'inherit', opacity: 0.6 },
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>

                {showFormattingToolbar && (
                  <Stack direction="row" spacing={0.25} sx={{ mb: 0.75 }}>
                    {[
                      { icon: 'mdi:format-bold', prefix: '*', suffix: '*', tooltip: 'Bold' },
                      { icon: 'mdi:format-italic', prefix: '_', suffix: '_', tooltip: 'Italic' },
                      { icon: 'mdi:format-strikethrough', prefix: '~', suffix: '~', tooltip: 'Strikethrough' },
                      { icon: 'mdi:code-tags', prefix: '```', suffix: '```', tooltip: 'Monospace' },
                    ].map((fmt) => (
                      <Tooltip key={fmt.tooltip} title={fmt.tooltip}>
                        <IconButton
                          size="small"
                          onClick={() => insertFormatting(fmt.prefix, fmt.suffix)}
                          sx={{
                            width: 28, height: 28, borderRadius: '6px',
                            color: 'text.secondary', bgcolor: 'rgba(0,0,0,0.03)',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                          }}
                        >
                          <Iconify icon={fmt.icon} width={15} />
                        </IconButton>
                      </Tooltip>
                    ))}
                  </Stack>
                )}
                <TextField
                  value={form.bodyText}
                  onChange={handleChange('bodyText')}
                  onFocus={() => setActiveField('bodyText')}
                  placeholder={'e.g. Hi {{1}}, your {{2}} appointment is confirmed.\n\nUse the chips above to insert patient details.'}
                  error={bodyOver}
                  multiline
                  minRows={4}
                  maxRows={24}
                  fullWidth
                  size="small"
                  sx={{
                    ...appleInputSx,
                    '& textarea': { resize: 'vertical' },
                  }}
                  helperText="Use the variable chips above to personalise for each patient. Drag the bottom-right corner to enlarge."
                  FormHelperTextProps={{ sx: { fontSize: '0.62rem', color: 'text.disabled', ml: 0.5, mt: 0.5 } }}
                />
                <CharCount current={form.bodyText.length} max={LIMITS.BODY} />
                {/* Trailing / leading-variable info note — purely informational,
                    mirrors what the BE does so the user isn't surprised by the
                    final approved text. Trailing: BE appends "."; Leading: BE
                    prepends "Hi ". Both are already reflected in the live preview
                    via fixLeadingTrailingVars applied in the previewTemplate memo. */}
                {(hasTrailingVar || hasLeadingVar) && (
                  <Box
                    sx={{
                      mt: 0.75,
                      bgcolor: 'rgba(59,130,246,0.06)',
                      border: '0.5px solid rgba(59,130,246,0.2)',
                      borderRadius: '10px',
                      p: 1.25,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <Iconify icon="mdi:information-outline" width={16} sx={{ color: '#2563eb', mt: 0.1, flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#1e40af', mb: 0.25 }}>
                        WhatsApp variable placement note
                      </Typography>
                      {hasTrailingVar && (
                        <Typography sx={{ fontSize: '0.68rem', color: '#2563eb', lineHeight: 1.45 }}>
                          Messages can&apos;t end on a variable — a full stop will be added after it automatically so it passes Meta approval. The preview reflects this.
                        </Typography>
                      )}
                      {hasLeadingVar && (
                        <Typography sx={{ fontSize: '0.68rem', color: '#2563eb', lineHeight: 1.45, mt: hasTrailingVar ? 0.5 : 0 }}>
                          Messages can&apos;t start with a variable — &ldquo;Hi &rdquo; will be prepended automatically. The preview reflects this.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                {/* Hardcoded-name warning — shown when heuristic detects a literal
                    first name in a greeting (e.g. "Hi Sarah,"). Nudge-only: does
                    not block submit. One-click replace swaps the detected word
                    with a First Name variable using the existing positional system. */}
                {hardcodedNameWarning && (
                  <Box
                    sx={{
                      mt: 0.75,
                      bgcolor: 'rgba(245,158,11,0.08)',
                      border: '0.5px solid rgba(245,158,11,0.25)',
                      borderRadius: '10px',
                      p: 1.25,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <Iconify icon="mdi:alert-outline" width={16} sx={{ color: '#b45309', mt: 0.1, flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#92400e', mb: 0.25 }}>
                        Looks like a specific name — use a variable instead?
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: '#b45309', lineHeight: 1.45 }}>
                        &ldquo;{hardcodedNameWarning}&rdquo; will be sent to every patient.{' '}
                        <Box
                          component="span"
                          onClick={handleReplaceHardcodedName}
                          sx={{ fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          Replace with First Name variable
                        </Box>
                        {' '}so each patient sees their own name.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              {showFooter && (
              <Box>
                <Typography sx={sectionLabelSx}>Footer</Typography>
                <TextField
                  value={form.footerText}
                  onChange={handleChange('footerText')}
                  placeholder="e.g. Reply STOP to opt out"
                  error={footerOver}
                  fullWidth
                  size="small"
                  sx={appleInputSx}
                  inputRef={footerInputRef}
                />
                {form.footerText.length > 0 && <CharCount current={form.footerText.length} max={LIMITS.FOOTER} />}
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    onClick={() => { setShowFooter(false); setForm((prev) => ({ ...prev, footerText: '' })); }}
                    startIcon={<Iconify icon="mdi:close" width={14} />}
                    sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'text.secondary', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                  >
                    Remove footer
                  </Button>
                </Box>
              </Box>
              )}


              {showCta && (
              <Box
                sx={{
                  bgcolor: '#f5f5f7',
                  borderRadius: '14px',
                  border: '0.5px solid rgba(0,0,0,0.06)',
                  p: 2,
                }}
              >
                <Typography sx={sectionLabelSx}>Call-to-action</Typography>
                <ToggleButtonGroup
                  value={form.buttonType}
                  exclusive
                  onChange={(_, val) => {
                    if (!val) return;
                    let defaultLabel = 'Book Now';
                    if (val === 'phone') defaultLabel = 'Call Us';
                    else if (val === 'form_link') defaultLabel = 'Complete Form';
                    setForm((prev) => ({
                      ...prev,
                      buttonType: val,
                      buttonLabel: val === 'none' ? prev.buttonLabel : defaultLabel,
                      buttonUrl: val === 'url' && !prev.buttonUrl ? practiceExamples.bookingUrl : prev.buttonUrl,
                      buttonPhone: val === 'phone' && !prev.buttonPhone ? practiceExamples.phone : prev.buttonPhone,
                    }));
                  }}
                  fullWidth
                  size="small"
                  sx={{
                    bgcolor: 'rgba(0,0,0,0.04)',
                    borderRadius: '8px',
                    p: '2px',
                    gap: 0,
                    '& .MuiToggleButtonGroup-grouped': {
                      border: 'none',
                      borderRadius: '6px !important',
                      flex: 1,
                      textTransform: 'none',
                      fontSize: '0.72rem',
                      fontWeight: 500,
                      py: 0.75,
                      color: 'text.secondary',
                      transition: 'all 0.2s ease',
                      '&.Mui-selected': {
                        bgcolor: '#fff',
                        color: 'text.primary',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08), 0 0 0 0.5px rgba(0,0,0,0.04)',
                        '&:hover': { bgcolor: '#fff' },
                      },
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                    },
                  }}
                >
                  {BUTTON_TYPES
                    .filter((bt) => !bt.formsOnly || isFormsTemplate)
                    .map((bt) => (
                      <ToggleButton key={bt.value} value={bt.value} disableRipple>
                        <Iconify icon={bt.icon} width={14} sx={{ mr: 0.5 }} />
                        {bt.label}
                      </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                {form.buttonType !== 'none' && (
                  <Stack spacing={1.5} sx={{ mt: 2 }}>
                    <TextField
                      value={form.buttonLabel}
                      onChange={handleChange('buttonLabel')}
                      placeholder="Button text"
                      label="Button label"
                      fullWidth
                      size="small"
                      sx={appleInputSx}
                    />
                    {form.buttonType === 'url' && (
                      <TextField
                        value={form.buttonUrl}
                        onChange={handleChange('buttonUrl')}
                        placeholder="https://book.example.com"
                        label="Booking URL"
                        fullWidth
                        size="small"
                        sx={appleInputSx}
                        InputProps={{
                          startAdornment: (
                            <Iconify icon="mdi:link-variant" width={15} sx={{ color: 'text.disabled', mr: 0.5 }} />
                          ),
                        }}
                        helperText={
                          practiceExamples.bookingUrl && form.buttonUrl === practiceExamples.bookingUrl
                            ? 'Auto-filled from your practice booking settings'
                            : 'Use https://example.com/{{1}} for a dynamic link'
                        }
                        FormHelperTextProps={{ sx: { fontSize: '0.62rem', color: practiceExamples.bookingUrl && form.buttonUrl === practiceExamples.bookingUrl ? '#30D158' : 'text.secondary', ml: 0.5 } }}
                      />
                    )}
                    {form.buttonType === 'phone' && (
                      <TextField
                        value={form.buttonPhone}
                        onChange={handleChange('buttonPhone')}
                        placeholder="+44 20 7123 4567"
                        label="Phone number"
                        fullWidth
                        size="small"
                        sx={appleInputSx}
                        InputProps={{
                          startAdornment: (
                            <Iconify icon="mdi:phone-outline" width={15} sx={{ color: 'text.disabled', mr: 0.5 }} />
                          ),
                        }}
                        helperText={
                          practiceExamples.phone && form.buttonPhone === practiceExamples.phone
                            ? 'Auto-filled from your practice contact details'
                            : undefined
                        }
                        FormHelperTextProps={{ sx: { fontSize: '0.62rem', color: '#30D158', ml: 0.5 } }}
                      />
                    )}
                    {form.buttonType === 'form_link' && (
                      // Display-only URL preview. The actual stored URL is
                      // ``…/consent/{{1}}`` because Meta requires a numeric
                      // placeholder in the URL-button suffix; we render the
                      // suffix as ``<unique-token>`` here so admins don't
                      // confuse it with the body's ``{{1}}`` placeholder
                      // (different components — Meta scopes placeholders
                      // per component, but the visual collision is
                      // confusing for humans).
                      <Box
                        sx={{
                          bgcolor: '#fff',
                          border: '0.5px solid rgba(0,0,0,0.08)',
                          borderRadius: '8px',
                          p: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Iconify icon="mdi:link-variant" width={15} sx={{ color: 'text.disabled' }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: '0.72rem', fontFamily: 'ui-monospace, SF Mono, monospace', color: 'text.primary' }}>
                            {(practiceExamples.formsBaseUrl || 'https://forms.example.com').replace(/\/$/, '')}/consent/
                            <Box component="span" sx={{ color: '#00AB55', fontWeight: 600 }}>{'<unique-token>'}</Box>
                          </Typography>
                          <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', mt: 0.25 }}>
                            Each patient gets a unique link — the token is generated at send time.
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Stack>
                )}
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    size="small"
                    onClick={() => { setShowCta(false); setForm((prev) => ({ ...prev, buttonType: 'none', buttonLabel: '', buttonUrl: '', buttonPhone: '' })); }}
                    startIcon={<Iconify icon="mdi:close" width={14} />}
                    sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'text.secondary', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                  >
                    Remove call-to-action
                  </Button>
                </Box>
              </Box>
              )}

              {/* Inline "+ Add …" affordances for optional sections that are
                  currently hidden. Each pill appears only while its section
                  is collapsed; clicking opens that one section inline. */}
              {(!showHeader || !showFooter || !showCta) && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
                  {!showHeader && (
                    <Button
                      size="small"
                      onClick={() => {
                        setShowHeader(true);
                        // Defer one frame so the TextField is actually mounted
                        // before we try to focus it.
                        setTimeout(() => headerInputRef.current?.focus(), 0);
                      }}
                      startIcon={<Iconify icon="mdi:plus" width={14} />}
                      sx={addPillSx}
                    >
                      Add header
                    </Button>
                  )}
                  {!showFooter && (
                    <Button
                      size="small"
                      onClick={() => {
                        setShowFooter(true);
                        setTimeout(() => footerInputRef.current?.focus(), 0);
                      }}
                      startIcon={<Iconify icon="mdi:plus" width={14} />}
                      sx={addPillSx}
                    >
                      Add footer
                    </Button>
                  )}
                  {!showCta && (
                    <Button
                      size="small"
                      onClick={() => {
                        setShowCta(true);
                        setForm((prev) => ({
                          ...prev,
                          buttonType: prev.buttonType === 'none' ? 'url' : prev.buttonType,
                        }));
                      }}
                      startIcon={<Iconify icon="mdi:plus" width={14} />}
                      sx={addPillSx}
                    >
                      Add call-to-action
                    </Button>
                  )}
                </Stack>
              )}

              {showAudienceSection && (
                <Box sx={{ pt: 1.5, mt: 0.5, borderTop: '0.5px solid rgba(0,0,0,0.08)' }}>
                  <WhatsAppAudienceRulesEditor
                    value={audienceDraft}
                    onChange={(next) => { setAudienceDraft(next); setAudienceSaved(false); }}
                    practiceTreatments={practiceTreatments}
                    disabled={!canManageAudience || audienceSaving}
                    dense
                  />
                  <Typography sx={{ fontSize: '0.68rem', color: '#86868b', mt: 1, display: 'block' }}>
                    Audience is stored in the CRM only — saving it does not send the
                    template to Meta for re-approval.
                  </Typography>
                  {canManageAudience && (audienceDirty || audienceSaved) && (
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mt: 1 }}>
                      {audienceSaved && !audienceDirty && (
                        <Typography sx={{ fontSize: '0.7rem', color: '#00AB55', fontWeight: 600 }}>
                          Audience saved
                        </Typography>
                      )}
                      {audienceDirty && (
                        <>
                          <Button
                            size="small"
                            onClick={() => setAudienceDraft(toRuleValue(editTemplate))}
                            disabled={audienceSaving}
                            sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#86868b' }}
                          >
                            Discard
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleSaveAudience}
                            disabled={audienceSaving || Boolean(validateRule(audienceDraft))}
                            sx={{
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              borderRadius: '8px',
                              bgcolor: '#00AB55',
                              '&:hover': { bgcolor: '#007B55' },
                            }}
                          >
                            {audienceSaving && (
                              <CircularProgress size={12} thickness={5} sx={{ color: '#fff', mr: 0.75 }} />
                            )}
                            {audienceSaving ? 'Saving' : 'Save audience'}
                          </Button>
                        </>
                      )}
                    </Stack>
                  )}
                </Box>
              )}
            </Stack>
          </Box>

          {/* ─── Right: Preview ─── */}
          <Box sx={{ width: 280, flexShrink: 0, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
            <Typography sx={sectionLabelSx}>Live Preview</Typography>
            {previewTemplate ? (
              <WhatsAppTemplatePreviewPanel
                template={previewTemplate}
                contextMapping={previewContextMapping}
                showVariables={totalVarCount > 0}
                hideTitle
              />
            ) : (
              <Box sx={{
                minHeight: 280,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#f5f5f7',
                borderRadius: '14px',
                border: '0.5px solid rgba(0,0,0,0.06)',
                p: 3,
              }}>
                <Iconify icon="mdi:whatsapp" width={36} sx={{ color: 'text.disabled', mb: 1 }} />
                <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled', textAlign: 'center' }}>
                  Start typing to see a live preview
                </Typography>
              </Box>
            )}
            {totalVarCount > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  onClick={() => setShowTestValues((v) => !v)}
                  endIcon={
                    <Iconify
                      icon={showTestValues ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                      width={14}
                    />
                  }
                  sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    fontSize: '0.72rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    px: 1,
                    py: 0.5,
                    borderRadius: '8px',
                    bgcolor: 'rgba(0,0,0,0.03)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                  }}
                >
                  Test with real data ({totalVarCount})
                </Button>
                {showTestValues && (
                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    {Array.from({ length: totalVarCount }, (_, i) => i + 1).map((num) => {
                      const mappedTag = tagMap[num];
                      return (
                        <TextField
                          key={num}
                          size="small"
                          placeholder={mappedTag ? mappedTag.example : `Variable {{${num}}}`}
                          value={testValues[num] || ''}
                          onChange={(e) => setTestValues(prev => ({ ...prev, [num]: e.target.value }))}
                          InputProps={{
                            startAdornment: (
                              <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.disabled', mr: 0.5, whiteSpace: 'nowrap' }}>
                                {`{{${num}}}`}
                              </Box>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px', fontSize: '0.78rem', bgcolor: '#f5f5f7',
                              '& fieldset': { borderColor: 'rgba(0,0,0,0.06)' },
                            },
                          }}
                        />
                      );
                    })}
                  </Stack>
                )}
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>

      {/* ─── Footer ─── */}
      <Box sx={{ borderTop: '0.5px solid rgba(0,0,0,0.06)' }}>
        {/* Validation hints — live list of WHY the submit button is disabled.
            Only shown when there are unmet requirements so the user always
            knows exactly what to fix. */}
        {validationHints.length > 0 && !submitting && (
          <Box sx={{ px: 2.5, pt: 1.25, pb: 0 }}>
            {validationHints.map((hint) => (
              <Box key={hint} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                <Iconify icon="mdi:alert-circle-outline" width={12} sx={{ color: 'error.main', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.68rem', color: 'error.main', lineHeight: 1.4 }}>
                  {hint}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              px: 2.5,
              color: 'text.secondary',
              bgcolor: 'rgba(0,0,0,0.04)',
              border: '0.5px solid rgba(0,0,0,0.06)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isValid || submitting}
            startIcon={<Iconify icon={submitting ? 'mdi:loading' : 'mdi:send-outline'} width={16} />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              px: 2.5,
              bgcolor: '#25D366',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 0 20px rgba(37,211,102,0.2)',
              '&:hover': {
                bgcolor: '#1fbd5a',
                boxShadow: '0 1px 3px rgba(0,0,0,0.15), 0 0 30px rgba(37,211,102,0.25)',
                transform: 'translateY(-0.5px)',
              },
              '&:active': { transform: 'translateY(0)' },
              '&.Mui-disabled': { bgcolor: 'rgba(37,211,102,0.4)', color: 'rgba(255,255,255,0.6)' },
            }}
          >
            {submitButtonLabel}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

RequestTemplateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  editTemplate: PropTypes.object,
  existingTemplateNames: PropTypes.arrayOf(PropTypes.string),
  cloneData: PropTypes.object,
  onUpdateAudience: PropTypes.func,
  practiceTreatments: PropTypes.array,
};

RequestTemplateDialog.defaultProps = {
  onSubmit: null,
  submitting: false,
  editTemplate: null,
  existingTemplateNames: [],
  cloneData: null,
  onUpdateAudience: null,
  practiceTreatments: [],
};

export default RequestTemplateDialog;
